import { calculateChecksum } from "@/components/Patient/fileChecksum";
import InsuranceCard from "@/components/Patient/Insurance/InsuranceCard";
import type { InsuranceFormValues } from "@/components/Patient/Insurance/insuranceSchema";
import InsuranceTable from "@/components/Patient/Insurance/InsuranceTable";
import type { PatientRegistrationFormValues } from "@/components/Patient/PatientRegistration";
import { AccordionContent } from "@/components/ui/accordion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import type useFileManager from "@/hooks/useFileManager";
import type { PatientInsuranceRead } from "@/types/emr/patient/patient";
import patientMasterDataApi from "@/types/emr/patient/patientMasterDataApi";
import query from "@/Utils/request/query";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

type Props = {
  form: UseFormReturn<PatientRegistrationFormValues>;
  patientId?: string;
  initialInsuranceRecords?: PatientInsuranceRead[];
  fileManager: ReturnType<typeof useFileManager>;
  onInsuranceDirtyChange?: (dirty: boolean) => void;
  onCardOpenChange?: (open: boolean) => void;
};

const InsuranceCoverageContent = ({
  form,
  patientId,
  initialInsuranceRecords = [],
  fileManager,
  onInsuranceDirtyChange,
  onCardOpenChange,
}: Props) => {
  const { t } = useTranslation();

  const [isCardOpen, setIsCardOpen] = useState(false);

  useEffect(() => {
    onCardOpenChange?.(isCardOpen);
  }, [isCardOpen, onCardOpenChange]);

  const [insuranceRecords, setInsuranceRecords] = useState<
    InsuranceFormValues[]
  >([]);

  const isHydratingRef = useRef(true);

  useEffect(() => {
    // Don't clobber an in-progress add/edit with fresh server data —
    // but DO re-sync any other time, including right after a save.
    if (isCardOpen) {
      return;
    }

    isHydratingRef.current = true;

    setInsuranceRecords(
      initialInsuranceRecords.map((record) => ({
        id: record.id,
        coverage_type: record.coverage_type?.id,
        government_scheme: record.government_scheme?.id,
        government_scheme_other: record.government_scheme_other ?? "",
        insurance_provider_name: record.insurance_provider_name ?? "",
        tpa_name: record.tpa_name ?? "",
        policy_or_card_number: record.policy_or_card_number ?? "",
        policy_validity_date: record.policy_validity_date ?? undefined,
        pre_authorization_required: record.pre_authorization_required,
        insurance_card_letter_file: record.insurance_card_letter_file,
        insurance_card_letter_pending: null,
      })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialInsuranceRecords]);

  useEffect(() => {
    form.setValue("insurance_records", insuranceRecords, {
      shouldDirty: !isHydratingRef.current,
    });

    isHydratingRef.current = false;
  }, [insuranceRecords, form]);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const [insuranceCardKey, setInsuranceCardKey] = useState(0);

  const [isCardDirty, setIsCardDirty] = useState(false);

  const [pendingEditIndex, setPendingEditIndex] = useState<number | null>(null);

  const [showEditDiscardDialog, setShowEditDiscardDialog] = useState(false);

  const [pendingNewInsurance, setPendingNewInsurance] = useState(false);

  const normalize = (value?: string | null) =>
    (value ?? "").trim().toUpperCase();

  type DuplicateReason = "document" | "record";

  const isDuplicateRecord = async (
    values: InsuranceFormValues,
    ignoreIndex?: number,
  ): Promise<DuplicateReason | false> => {
    const incomingPending = values.insurance_card_letter_pending;

    const incomingPendingChecksum = incomingPending
      ? await calculateChecksum(incomingPending)
      : null;

    for (const [index, record] of insuranceRecords.entries()) {
      if (index === ignoreIndex) {
        continue;
      }

      if (record.coverage_type === values.coverage_type) {
        const coverageCode = coverageTypes.find(
          (item) => item.id === values.coverage_type,
        )?.code;

        let isFieldDuplicate = false;

        switch (coverageCode) {
          case "government_scheme":
            isFieldDuplicate =
              record.government_scheme === values.government_scheme &&
              normalize(record.government_scheme_other) ===
                normalize(values.government_scheme_other) &&
              normalize(record.policy_or_card_number) ===
                normalize(values.policy_or_card_number);
            break;

          case "private_insurance":
            isFieldDuplicate =
              normalize(record.insurance_provider_name) ===
                normalize(values.insurance_provider_name) &&
              normalize(record.policy_or_card_number) ===
                normalize(values.policy_or_card_number);
            break;

          case "tpa":
            isFieldDuplicate =
              normalize(record.insurance_provider_name) ===
                normalize(values.insurance_provider_name) &&
              normalize(record.tpa_name) === normalize(values.tpa_name) &&
              normalize(record.policy_or_card_number) ===
                normalize(values.policy_or_card_number);
            break;

          case "corporate":
            isFieldDuplicate =
              normalize(record.insurance_provider_name) ===
                normalize(values.insurance_provider_name) &&
              normalize(record.policy_or_card_number) ===
                normalize(values.policy_or_card_number);
            break;
        }

        if (isFieldDuplicate) {
          return "record";
        }
      }

      const incomingFile = values.insurance_card_letter_file;
      const existingFile = record.insurance_card_letter_file;

      // Same uploaded file on the same record while editing
      if (
        incomingFile?.id &&
        existingFile?.id &&
        incomingFile.id === existingFile.id
      ) {
        continue;
      }

      if (incomingFile && existingFile) {
        // Preferred: checksum comparison
        if (
          incomingFile.checksum &&
          existingFile.checksum &&
          incomingFile.checksum === existingFile.checksum
        ) {
          return "document";
        }

        // Fallback for legacy uploads
        if (
          (!incomingFile.checksum || !existingFile.checksum) &&
          incomingFile.name === existingFile.name
        ) {
          return "document";
        }
      }

      if (incomingPending && record.insurance_card_letter_pending) {
        const existingPendingChecksum = await calculateChecksum(
          record.insurance_card_letter_pending,
        );

        if (incomingPendingChecksum === existingPendingChecksum) {
          return "document";
        }
      }
    }

    return false;
  };

  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const openFreshInsuranceCard = () => {
    setPendingNewInsurance(false);
    setPendingEditIndex(null);

    setEditingIndex(null);

    setInsuranceCardKey((k) => k + 1);

    setIsCardOpen(true);
    setIsCardDirty(false);

    onInsuranceDirtyChange?.(false);
  };

  const handleAddInsurance = async (
    values: InsuranceFormValues,
  ): Promise<boolean> => {
    const duplicate = await isDuplicateRecord(
      values,
      editingIndex ?? undefined,
    );

    if (duplicate === "document") {
      toast.error(t("duplicate_insurance_document"));
      return false;
    }

    if (duplicate === "record") {
      toast.error(t("duplicate_insurance_record"));
      return false;
    }

    if (editingIndex !== null) {
      setInsuranceRecords((prev) => {
        const next = prev.map((item, index) =>
          index === editingIndex
            ? {
                ...item,
                ...values,
                id: item.id,
                localId: item.localId,
              }
            : item,
        );

        return next;
      });

      setEditingIndex(null);
    } else {
      setInsuranceRecords((prev) => [
        ...prev,
        {
          ...values,
          localId: crypto.randomUUID(),
        },
      ]);
    }

    onInsuranceDirtyChange?.(false);
    setIsCardOpen(false);

    return true;
  };

  const handleEditInsurance = (index: number) => {
    if (isCardOpen && isCardDirty) {
      setPendingEditIndex(index);
      setShowEditDiscardDialog(true);
      return;
    }

    setInsuranceCardKey((k) => k + 1);
    setEditingIndex(index);
    setIsCardOpen(true);
    setIsCardDirty(false);
    onInsuranceDirtyChange?.(false);
  };

  const handleDeleteInsurance = (index: number) => {
    setInsuranceRecords((prev) => prev.filter((_, i) => i !== index));

    if (editingIndex === index) {
      setEditingIndex(null);
      setIsCardOpen(false);
    }
  };

  const coverageTypesQuery = useQuery({
    queryKey: ["coverage_types"],
    queryFn: query.paginated(patientMasterDataApi.listCoverageTypes),
  });

  const governmentSchemesQuery = useQuery({
    queryKey: ["government_schemes"],
    queryFn: query.paginated(patientMasterDataApi.listGovernmentSchemes),
  });

  const coverageTypes = coverageTypesQuery.data?.results ?? [];

  const governmentSchemes = governmentSchemesQuery.data?.results ?? [];

  const insuranceToDelete =
    deleteIndex !== null ? insuranceRecords[deleteIndex] : null;

  const coverageTypeName = coverageTypes.find(
    (item) => item.id === insuranceToDelete?.coverage_type,
  )?.name;

  const selectedGovernmentScheme = governmentSchemes.find(
    (item) => item.id === insuranceToDelete?.government_scheme,
  );

  const governmentSchemeDisplayName = selectedGovernmentScheme?.is_other
    ? insuranceToDelete?.government_scheme_other
    : selectedGovernmentScheme?.name;

  return (
    <AccordionContent className="px-1 flex flex-col gap-6">
      <div className="flex justify-end">
        <Button
          type="button"
          variant="default"
          onClick={() => {
            if (!isCardOpen) {
              openFreshInsuranceCard();
              return;
            }

            // Empty "Add" card already open.
            if (editingIndex === null && !isCardDirty) {
              return;
            }

            setPendingNewInsurance(true);
            setShowEditDiscardDialog(true);
          }}
        >
          {t("add_new_insurance")}
        </Button>
      </div>
      {insuranceRecords.length === 0 && !isCardOpen && (
        <div className="rounded border border-dashed p-4 text-center">
          <p className="text-sm font-medium text-gray-700">
            {t("no_insurance_records_found")}
          </p>
        </div>
      )}

      {insuranceRecords.length > 0 && (
        <InsuranceTable
          records={insuranceRecords}
          patientId={patientId}
          coverageTypes={coverageTypes}
          governmentSchemes={governmentSchemes}
          onEdit={handleEditInsurance}
          onDelete={(index) => {
            setDeleteIndex(index);
            setShowDeleteDialog(true);
          }}
          fileManager={fileManager}
        />
      )}

      {isCardOpen && (
        <InsuranceCard
          key={insuranceCardKey}
          coverageTypes={coverageTypes}
          governmentSchemes={governmentSchemes}
          patientId={patientId}
          initialValues={
            editingIndex !== null ? insuranceRecords[editingIndex] : undefined
          }
          onAdd={handleAddInsurance}
          onCancel={() => {
            setEditingIndex(null);

            setIsCardOpen(false);
            onInsuranceDirtyChange?.(false);
          }}
          onDirtyChange={(dirty) => {
            setIsCardDirty(dirty);
            onInsuranceDirtyChange?.(dirty);
          }}
        />
      )}

      <AlertDialog
        open={showEditDiscardDialog}
        onOpenChange={(open) => {
          setShowEditDiscardDialog(open);

          if (!open) {
            setPendingNewInsurance(false);
            setPendingEditIndex(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingNewInsurance
                ? t("start_new_insurance")
                : t("discard_unsaved_insurance")}
            </AlertDialogTitle>

            <AlertDialogDescription>
              {pendingNewInsurance
                ? t("start_new_insurance_message")
                : t("discard_unsaved_insurance_message")}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>

            <AlertDialogAction
              onClick={() => {
                if (pendingNewInsurance) {
                  setPendingNewInsurance(false);
                  setShowEditDiscardDialog(false);

                  openFreshInsuranceCard();
                  return;
                }

                if (pendingEditIndex === null) {
                  return;
                }

                setInsuranceCardKey((k) => k + 1);
                setEditingIndex(pendingEditIndex);

                setPendingEditIndex(null);

                setShowEditDiscardDialog(false);

                setIsCardDirty(false);

                onInsuranceDirtyChange?.(false);

                setIsCardOpen(true);
              }}
            >
              {pendingNewInsurance ? t("start_new") : t("discard")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("delete_insurance")}</AlertDialogTitle>

            <AlertDialogDescription>
              <div className="space-y-1">
                <p>{t("delete_insurance_confirmation")}</p>

                {insuranceToDelete && (
                  <div className="mt-2 text-sm text-muted-foreground space-y-1">
                    <div>
                      <strong>{t("coverage_type")}:</strong>{" "}
                      {coverageTypeName || "-"}
                    </div>

                    {governmentSchemeDisplayName && (
                      <div>
                        <strong>{t("government_scheme")}:</strong>{" "}
                        {governmentSchemeDisplayName}
                      </div>
                    )}

                    {insuranceToDelete.insurance_provider_name && (
                      <div>
                        <strong>{t("insurance_provider_name")}:</strong>{" "}
                        {insuranceToDelete.insurance_provider_name}
                      </div>
                    )}

                    {insuranceToDelete.tpa_name && (
                      <div>
                        <strong>{t("tpa_name")}:</strong>{" "}
                        {insuranceToDelete.tpa_name}
                      </div>
                    )}

                    <div>
                      <strong>{t("policy_or_card_number")}:</strong>{" "}
                      {insuranceToDelete.policy_or_card_number || "-"}
                    </div>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>

            <AlertDialogAction
              onClick={() => {
                if (deleteIndex !== null) {
                  handleDeleteInsurance(deleteIndex);
                }

                setDeleteIndex(null);
                setShowDeleteDialog(false);
              }}
            >
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AccordionContent>
  );
};

export default InsuranceCoverageContent;
